import React, { useMemo, useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const ROLE_LABELS = { admin: 'Admin', salarie: 'Salarié' };
const TYPE_LABELS = { salarie: 'Salarié', freelance: 'Freelance' };
const TYPE_COLORS = {
  salarie: 'border-blue-400 bg-blue-50 text-[#002FA7]',
  freelance: 'border-amber-400 bg-amber-50 text-amber-800',
};
const AGENCE_LABELS = { paris: 'Paris', casablanca: 'Casablanca' };
const AGENCE_COLORS = {
  paris: 'border-zinc-400 bg-zinc-50 text-zinc-700',
  casablanca: 'border-orange-400 bg-orange-50 text-orange-800',
};

const emptyForm = {
  email: '', password: '', nom: '', prenom: '',
  role: 'salarie', type_contrat: 'salarie', poste: 'Architecte',
  agences: ['paris'],
  taux_horaire: 40, actif: true,
  salaire_brut: 0, cotisation_urssaf: 0, mutuelle: 0, ag2r: 0,
};

export default function Salaries() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sort, toggle] = useSort('nom', 'asc');

  const load = async () => setList((await api.get('/users')).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      email: s.email, password: '', nom: s.nom, prenom: s.prenom,
      role: s.role, type_contrat: s.type_contrat || 'salarie',
      poste: s.poste || '',
      agences: (s.agences && s.agences.length > 0) ? s.agences : ['paris'],
      taux_horaire: s.taux_horaire || 0, actif: s.actif,
      salaire_brut: s.salaire_brut || 0, cotisation_urssaf: s.cotisation_urssaf || 0,
      mutuelle: s.mutuelle || 0, ag2r: s.ag2r || 0,
    });
    setEditingId(s.id); setOpen(true);
  };

  const toggleAgence = (a) => {
    setForm(f => {
      const has = f.agences.includes(a);
      const next = has ? f.agences.filter(x => x !== a) : [...f.agences, a];
      return { ...f, agences: next.length > 0 ? next : f.agences };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        taux_horaire: Number(form.taux_horaire),
        salaire_brut: Number(form.salaire_brut),
        cotisation_urssaf: Number(form.cotisation_urssaf),
        mutuelle: Number(form.mutuelle), ag2r: Number(form.ag2r),
      };
      if (editingId) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editingId}`, payload); toast.success('Mis à jour');
      } else {
        if (!payload.password) payload.password = 'test1234';
        await api.post('/users', payload); toast.success('Créé');
      }
      setOpen(false); load();
    } catch (err) {
      console.error('[Salaries] create/update error', err, err.response?.data);
      const detail = err.response?.data?.detail;
      let msg = 'Erreur';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        msg = detail.map(d => `${(d.loc || []).join('.')} : ${d.msg}`).join(' | ');
      } else if (err.message) {
        msg = `${err.response?.status || ''} ${err.message}`;
      }
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await api.delete(`/users/${id}`); toast.success('Supprimé'); load();
  };

  const coutMensuelTotal = (s) => {
    if (s.type_contrat === 'freelance') return 0; // coût via facture projet
    return (s.salaire_brut || 0) + (s.cotisation_urssaf || 0) + (s.mutuelle || 0) + (s.ag2r || 0);
  };

  const isFreelance = form.type_contrat === 'freelance';

  return (
    <Layout
      title="Salariés"
      subtitle={`${list.length} utilisateurs — ${list.filter(u => u.type_contrat === 'freelance').length} freelances`}
      actions={
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button onClick={openCreate} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="nouveau-salarie-btn">
              <Plus size={16} className="mr-2" /> Nouveau
            </Button>
          </SheetTrigger>
          <SheetContent className="rounded-none w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader><SheetTitle className="font-display font-bold text-2xl tracking-tight">{editingId ? 'Modifier' : 'Nouveau compte'}</SheetTitle></SheetHeader>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom *</Label><Input required className="rounded-none" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
                <div><Label>Nom *</Label><Input required className="rounded-none" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
              </div>
              <div><Label>Email *</Label><Input required type="email" className="rounded-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Mot de passe {editingId && <span className="text-xs text-zinc-500">(vide = inchangé)</span>}</Label><Input type="text" className="rounded-none font-mono" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rôle *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="salarie">Utilisateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type contrat *</Label>
                  <Select value={form.type_contrat} onValueChange={(v) => setForm({ ...form, type_contrat: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="salarie">Salarié</SelectItem>
                      <SelectItem value="freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div><Label>Poste</Label><Input className="rounded-none" value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} /></div>

              <div>
                <Label>Agence(s) de rattachement *</Label>
                <div className="flex gap-2 mt-2" data-testid="form-agences">
                  {Object.entries(AGENCE_LABELS).map(([k, label]) => {
                    const active = form.agences.includes(k);
                    return (
                      <button key={k} type="button" onClick={() => toggleAgence(k)}
                        className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${active ? 'border-[#002FA7] bg-[#002FA7] text-white' : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'}`}
                        data-testid={`form-agence-${k}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">Sert au reporting de rentabilité par pôle.</p>
              </div>

              <div><Label>Taux horaire (coût imputation projets) €/h *</Label><Input required type="number" step="0.01" className="rounded-none font-mono" value={form.taux_horaire} onChange={(e) => setForm({ ...form, taux_horaire: e.target.value })} /></div>

              {!isFreelance ? (
                <div className="bg-blue-50 border border-[#002FA7]/30 p-4 space-y-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-medium">Frais salarié mensuels</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Salaire brut</Label><Input type="number" className="rounded-none font-mono h-9" value={form.salaire_brut} onChange={(e) => setForm({ ...form, salaire_brut: e.target.value })} /></div>
                    <div><Label className="text-xs">Cotisation URSSAF</Label><Input type="number" className="rounded-none font-mono h-9" value={form.cotisation_urssaf} onChange={(e) => setForm({ ...form, cotisation_urssaf: e.target.value })} /></div>
                    <div><Label className="text-xs">Mutuelle</Label><Input type="number" className="rounded-none font-mono h-9" value={form.mutuelle} onChange={(e) => setForm({ ...form, mutuelle: e.target.value })} /></div>
                    <div><Label className="text-xs">AG2R (retraite)</Label><Input type="number" className="rounded-none font-mono h-9" value={form.ag2r} onChange={(e) => setForm({ ...form, ag2r: e.target.value })} /></div>
                  </div>
                  <div className="pt-2 border-t border-[#002FA7]/20 flex justify-between text-sm font-mono">
                    <span>Total mensuel</span>
                    <span className="font-medium">{fmtEUR((Number(form.salaire_brut) || 0) + (Number(form.cotisation_urssaf) || 0) + (Number(form.mutuelle) || 0) + (Number(form.ag2r) || 0))}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-400 p-4 text-xs text-amber-900">
                  <strong>Freelance</strong> : les coûts sont imputés via factures de prestation par projet (non comptabilisés ici). Le taux horaire est utilisé pour le calcul du coût projet dans les dépenses des heures pointées.
                </div>
              )}

              <div className="flex items-center justify-between border border-zinc-200 p-3">
                <Label>Actif</Label>
                <Switch checked={form.actif} onCheckedChange={(v) => setForm({ ...form, actif: v })} />
              </div>
              <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]">{editingId ? 'Mettre à jour' : 'Créer'}</Button>
            </form>
          </SheetContent>
        </Sheet>
      }
    >
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="salaries-table">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              <SortHeader sort={sort} onToggle={toggle} k="nom" label="Nom" />
              <SortHeader sort={sort} onToggle={toggle} k="role" label="Rôle" />
              <SortHeader sort={sort} onToggle={toggle} k="type_contrat" label="Type" />
              <SortHeader sort={sort} onToggle={toggle} k="poste" label="Poste" />
              <SortHeader sort={sort} onToggle={toggle} k="email" label="Email" />
              <SortHeader sort={sort} onToggle={toggle} k="taux_horaire" label="Taux €/h" align="right" />
              <SortHeader sort={sort} onToggle={toggle} k="cout_mensuel" label="Coût mens." align="right" />
              <SortHeader sort={sort} onToggle={toggle} k="actif" label="Statut" />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortArr(list, sort, (u, k) => k === 'cout_mensuel' ? coutMensuelTotal(u) : u[k]).map((s) => (
              <tr key={s.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                <td className="px-3 py-3 font-medium">
                  <button onClick={() => setDetail(s)} className="hover:text-[#002FA7] hover:underline text-left" data-testid={`detail-${s.id}`}>
                    {s.prenom} {s.nom}
                  </button>
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${s.role === 'admin' ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-zinc-300 bg-zinc-50 text-zinc-700'}`}>{ROLE_LABELS[s.role]}</span>
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${TYPE_COLORS[s.type_contrat || 'salarie']}`}>{TYPE_LABELS[s.type_contrat || 'salarie']}</span>
                </td>
                <td className="px-3 py-3 text-zinc-600 text-xs">{s.poste || '—'}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {(s.agences && s.agences.length > 0 ? s.agences : ['paris']).map(a => (
                      <span key={a} className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${AGENCE_COLORS[a] || 'border-zinc-300 bg-zinc-50 text-zinc-600'}`}>
                        {AGENCE_LABELS[a] || a}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-zinc-600">{s.email}</td>
                <td className="px-3 py-3 font-mono text-right">{fmtEUR(s.taux_horaire)}</td>
                <td className="px-3 py-3 font-mono text-right text-xs">{s.type_contrat === 'freelance' ? '—' : fmtEUR(coutMensuelTotal(s))}</td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${s.actif ? 'border-emerald-600 text-emerald-700 bg-emerald-50' : 'border-zinc-400 text-zinc-500 bg-zinc-50'}`}>{s.actif ? 'Actif' : 'Inactif'}</span>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-zinc-200"><Pencil size={13} /></button>
                    <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-rose-100 text-rose-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <SheetContent className="rounded-none w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display font-bold text-2xl tracking-tight">{detail.prenom} {detail.nom}</SheetTitle>
              <p className="text-xs text-zinc-500 font-mono">{detail.email}</p>
            </SheetHeader>
            <div className="space-y-4 mt-6 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Rôle</div>
                  <div className="font-medium mt-1">{ROLE_LABELS[detail.role]}</div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Type</div>
                  <div className="font-medium mt-1">{TYPE_LABELS[detail.type_contrat || 'salarie']}</div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Poste</div>
                  <div className="font-medium mt-1">{detail.poste || '—'}</div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Agence(s)</div>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {(detail.agences && detail.agences.length > 0 ? detail.agences : ['paris']).map(a => (
                      <span key={a} className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${AGENCE_COLORS[a] || 'border-zinc-300'}`}>
                        {AGENCE_LABELS[a] || a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 p-3 border-l-4 border-l-[#002FA7]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Taux horaire</div>
                  <div className="font-mono font-medium mt-1 text-xl">{fmtEUR(detail.taux_horaire)}/h</div>
                </div>
              </div>

              {detail.type_contrat !== 'freelance' ? (
                <div className="bg-white border border-zinc-200 p-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Frais mensuels</div>
                  {[['Salaire brut', detail.salaire_brut],
                    ['Cotisation URSSAF', detail.cotisation_urssaf],
                    ['Mutuelle', detail.mutuelle],
                    ['AG2R', detail.ag2r]].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-zinc-600">{k}</span>
                      <span className="font-mono">{fmtEUR(v || 0)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-medium pt-2 border-t border-zinc-200">
                    <span>Total</span>
                    <span className="font-mono">{fmtEUR(coutMensuelTotal(detail))}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900">
                  Freelance : coûts imputés via factures de prestation projet.
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      <div className="mt-4 text-xs text-zinc-500 font-mono">
        <KeyRound size={12} className="inline mr-1" /> Mot de passe par défaut : <span className="font-medium">test1234</span>
      </div>
    </Layout>
  );
}
